import React from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { stringify } from "querystring";
import { Link, useHistory } from "react-router-dom";
import { requestOK } from "redux-thunk-kit";
import googlelogo from "~/assets/img/v2/googlelogo.png";
import {
  Col,
  Logo,
  Row,
  textVr,
  UiButton,
  UiCheckbox,
  UiDivider,
  UiFormControl,
  UiFormErrorMessage,
  UiFormLabel,
  UiImage,
  UiInput,
  UiInputGroup,
  UiInputLeftElement,
  UiLink,
  UiPrimaryButton,
  UiText,
  useMediaQuery,
} from "~/components/elements";
import { PageLayout } from "~/components/layouts/PageLayout";
import { appConfig } from "~/config";
import { ERROR_STATUS_CODE, PAGE_URLS, PAGES, QUERY_MOBILE, SOCIAL_LOGIN_PLATFORMS } from "~/constants";
import { useAppDispatch, useInput, useNotification, useRequestState } from "~/hooks";
import { loginByEmailMisc, socialLoginMisc } from "~/redux/auth/thunk";
import { getPageQuery } from "~/utils/helpers";
import PasswordInput from "~/components/shared/PasswordInput";
import { useGoogleLogin } from "@react-oauth/google";
import { MailIcon } from "~/components/icons/SvgIcons";

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const { addMessage } = useNotification(dispatch);
  const history = useHistory();
  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`, {
    ssr: false,
  });
  const rules = {
    email: { presence: true, email: { message: "^Please enter a valid email address" } },
    password(value: string) {
      if (value) {
        return { length: { minimum: 6 } };
      }
      return { presence: true };
    },
  };
  const [inputs, setInputs, { validation }] = useInput({ email: "", password: "", rememberMe: false }, { rules });

  const { loading } = useRequestState(loginByEmailMisc);

  const onLoginSuccess = (redirectAfterLogin?: string) => {
    // Try to redirect to the very first URL
    const urlParams = new URL(window.location.href);
    const queryParams = getPageQuery();
    let { redirect } = queryParams as { redirect: string };
    if (redirectAfterLogin) {
      history.replace(redirectAfterLogin);
      return;
    }
    if (redirect) {
      const redirectUrlParams = new URL(redirect);
      if (redirectUrlParams.origin === urlParams.origin) {
        redirect = redirect.substr(urlParams.origin.length);
        if (redirect.match(/^\/.*#/)) {
          redirect = redirect.substr(redirect.indexOf("#") + 1);
        }
      } else {
        window.location.href = "/";
        return;
      }
    }
    history.replace(redirect || PAGES.DASHBOARD);
  };

  const redirectTo = stringify({
    redirect: window.location.href,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e?.preventDefault();
    if (validation.getErrors()) {
      return;
    }
    const resAction = await dispatch(loginByEmailMisc({ data: { ...inputs } }));
    if (requestOK(resAction, loginByEmailMisc)) {
      const result = unwrapResult(resAction);
      if (result.errCode === ERROR_STATUS_CODE.NOT_VERIFIED_ACCOUNT) {
        history.push({
          pathname: PAGE_URLS.EMAIL_CONFIRMATION,
          state: { userId: result.contexts.user_id, email: inputs.email },
        });
        return;
      }
      onLoginSuccess();
    }
  };

  const onSignInWithGoogle = useGoogleLogin({
    flow: "auth-code",
    scope: [
      "openid",
      "https://www.googleapis.com/auth/userinfo.profile",
      "https://www.googleapis.com/auth/userinfo.email",
    ].join(" "),
    onSuccess: async resp => {
      const { code } = resp;

      const resAction = await dispatch(
        socialLoginMisc({
          data: {
            flow: "auth-code",
            token: code || "",
            provider: SOCIAL_LOGIN_PLATFORMS.GOOGLE,
            clientId: appConfig.googleClientId,
          },
        })
      );
      if (requestOK(resAction, socialLoginMisc)) {
        onLoginSuccess();
      }
    },
    onError: errorResponse => {
      console.log("errorResponse: ", errorResponse);
    },
  });

  return (
    <PageLayout
      metaTagsConfig={{
        title: `Bettershot - Sign in`,
        description: `Get 120 professional photoshoots in 3 hours using AI. Elevate your personal brand with corporate photoshoots.`,
        charset: "utf8",
        lang: "en",
        metas: [
          {
            name: "keywords",
            content: "corporate photoshoot,professional photoshoot,bettershot ai,linkedin profile photo,ai photoshoot",
          },
        ],
        links: [{ rel: "canonical", href: window.location.href }],
      }}
    >
      <Col alignItems="center">
        <Col mt={{ base: 8, md: 0 }} px={{ base: 4 }} maxW="29.625rem" w="full">
          <Logo containerProps={{ justifyContent: "center" }} />
          <UiText vr="headline3" fontSize={{ base: "1.5rem", md: "1.75rem" }} textAlign="center" mt={12}>
            Sign in
          </UiText>
          <form onSubmit={handleSubmit}>
            <Col mx="auto" mt={4}>
              <UiFormControl isRequired isInvalid={!!validation?.errors?.email}>
                <UiFormLabel {...textVr("body2")}>Email</UiFormLabel>
                <UiInputGroup>
                  <UiInputLeftElement pointerEvents="none">
                    <MailIcon />
                  </UiInputLeftElement>
                  <UiInput
                    ps="10"
                    placeholder="Enter your email"
                    name="email"
                    type="email"
                    defaultValue={inputs.email}
                    _invalid={{ boxShadow: "none", borderColor: "red" }}
                    onChange={({ target }) => setInputs({ email: target.value })}
                  />
                </UiInputGroup>
                {validation?.errors?.email?.[0] && (
                  <UiFormErrorMessage mt={1} fontSize="12px" color="red">
                    {validation?.errors?.email?.[0]}
                  </UiFormErrorMessage>
                )}
              </UiFormControl>

              <UiFormControl {...textVr("body2")} isRequired isInvalid={!!validation?.errors?.password} mt={4}>
                <UiFormLabel fontWeight="400">Password</UiFormLabel>
                <PasswordInput
                  name="password"
                  defaultValue={inputs.password}
                  _invalid={{ boxShadow: "none", borderColor: "red" }}
                  onChange={({ target }) => setInputs({ password: target.value })}
                />
                {validation?.errors?.password?.[0] && (
                  <UiFormErrorMessage mt={1} fontSize="12px" color="red">
                    {validation?.errors?.password?.[0]}
                  </UiFormErrorMessage>
                )}
              </UiFormControl>
              <Row justifyContent="space-between" mt={7}>
                <UiCheckbox isChecked={inputs.rememberMe} onChange={e => setInputs({ rememberMe: e.target.checked })}>
                  <UiText vr="body2">Remember me</UiText>
                </UiCheckbox>
                <UiLink
                  as={Link}
                  to={PAGE_URLS.FORGOT_PASSWORD}
                  alignSelf="flex-start"
                  _focusVisible={{ boxShadow: "none" }}
                >
                  <UiText vr="title2" color="blue">
                    Forgot password?
                  </UiText>
                </UiLink>
              </Row>
              <UiPrimaryButton
                isLoading={loading}
                type="submit"
                onClick={handleSubmit}
                isDisabled={!(inputs.email && inputs.password)}
                mt={4}
              >
                Sign in
              </UiPrimaryButton>
            </Col>
          </form>
          <UiText vr="body2" mx="auto" mt={3}>
            Don&apos;t have an account?
            <UiLink as={Link} to={{ pathname: PAGE_URLS.REGISTER, search: window.location.search }} fontWeight="700">
              &nbsp;Sign up for free&nbsp;
            </UiLink>
          </UiText>
          <UiDivider text="Or" containerProps={{ mt: 3 }} />
          <UiButton
            variant="outline"
            leftIcon={<UiImage src={googlelogo} boxSize="6" />}
            onClick={() => onSignInWithGoogle()}
            mt={2}
            borderColor="border"
            bg="transparent"
            color="darkblue"
            _hover={{ bg: "transparent", color: "border" }}
          >
            Sign in with Google
          </UiButton>
        </Col>
      </Col>
    </PageLayout>
  );
}
